import { useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { AppCookie } from 'utils/cookies'
import { setRandomString } from 'utils/misc'
import { buildHydraLogoutUrl } from 'utils/ory'
import { validateRedirectProtocol } from 'utils/security'

export const LOGOUT_REASON_KEY = 'logoutReason'
export const LOGOUT_REASON_EXPIRED = 'expired'

/**
 * Reads the logout reason stashed by useLogout before the Hydra round-trip,
 * so /expired can tell an automatic logout (idle timer, failed token refresh)
 * from a manual one. Returns `undefined` until mounted (sessionStorage is
 * client-only) — render nothing reason-dependent in that window so the
 * server markup stays deterministic and no wrong-variant copy flashes.
 */
export const useLogoutReason = () => {
  const [reason, setReason] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    setReason(sessionStorage.getItem(LOGOUT_REASON_KEY))
  }, [])

  return reason
}

/**
 * hook used for logging out,
 * clearing cache and redirecting
 * to hydra for session reset
 */
interface IUseLogout {
  /**
   * Delay before logging out,
   * useful for showing loading screen
   */
  delay?: number
  /**
   * Redirect url to hydra,
   * or can just use env var:
   * NEXT_PUBLIC_HYDRA_URL
   */
  hydraUrl?: string
  /** param to direct back after logout */
  redirectUri?: string
  /** function to delete app specific cookies */
  deleteCookies: () => void
  /* id token for specific app */
  idToken: AppCookie
  /**
   * Longer-lived copy of the id token, read when the `idToken` cookie has
   * already expired. Hydra accepts an expired id_token_hint, and only with a
   * hint does it honor `redirectUri` — without one the user lands on Hydra's
   * default post-logout page, which belongs to the partner app.
   */
  idTokenHint?: AppCookie
  /* if is impersonation, remove some real-case treatments */
  isImpersonation?: boolean
  /** localStorage keys to keep through the session-teardown wipe */
  preserveKeys?: string[]
}

// Defensive: js-cookie only removes a cookie when the path/domain attributes
// match those used at set(). Our app cookies today rely on js-cookie's default
// path '/', so Cookies.remove(name) already works — but pinning path here
// guards against future call sites that set a narrower path.
const removeCookie = (name: AppCookie) => Cookies.remove(name, { path: '/' })

/**
 * Wipes localStorage for session teardown while preserving a small allowlist
 * of keys that must survive logout (e.g. one-and-done "seen" flags that are
 * per-browser, not per-session). Snapshot/clear/restore rather than
 * selective removal, since it still fully clears anything NOT in the
 * allowlist in one pass.
 */
const clearLocalStorageExcept = (preserveKeys: string[]) => {
  if (preserveKeys.length === 0) {
    localStorage.clear()
    return
  }
  const preserved = preserveKeys
    .map(key => [key, localStorage.getItem(key)] as const)
    .filter(([, value]) => value !== null)
  localStorage.clear()
  for (const [key, value] of preserved)
    localStorage.setItem(key, value as string)
}

export const deleteCookiesAdmin = () => {
  removeCookie(AppCookie.TokenAdmin)
  removeCookie(AppCookie.RefreshAdmin)
}

export const deleteCookiesPartner = () => {
  removeCookie(AppCookie.TokenPartner)
  removeCookie(AppCookie.RefreshPartner)
  removeCookie(AppCookie.PartnerId)
  removeCookie(AppCookie.Impersonating)
}

export const deleteAllCookies = () => {
  Object.values(AppCookie).map(cookie => {
    removeCookie(cookie)
  })
}

export const deleteCookiesCustomer = () => {
  removeCookie(AppCookie.TokenCustomer)
  removeCookie(AppCookie.RefreshCustomer)
  removeCookie(AppCookie.IdTokenHintCustomer)
  removeCookie(AppCookie.CustomerId)
  removeCookie(AppCookie.Impersonating)
}

export const useClearCache = (
  deleteCookies: () => void,
  preserveKeys: string[] = []
) => {
  const client = useApolloClient()

  useEffect(() => {
    client.cache.reset()
    clearLocalStorageExcept(preserveKeys)
    deleteCookies()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot session teardown on mount; re-running on identity changes would wipe state again
  }, [])
}

export const useRedirectByState = () => {
  const {
    query: { state },
    push
  } = useRouter()
  const rawRedirectUri = (state as string)?.split('-')[1]
  // Validate extracted redirect — protocol-only, domain may be a custom partner domain
  const redirectUri = validateRedirectProtocol(rawRedirectUri)
  if (redirectUri) {
    push(redirectUri)
  }
}

export const useLogout = ({
  delay = 1000,
  hydraUrl,
  redirectUri,
  deleteCookies,
  idToken,
  idTokenHint,
  isImpersonation = false,
  preserveKeys = []
}: IUseLogout) => {
  const client = useApolloClient()
  const token =
    Cookies.get(idToken) || (idTokenHint ? Cookies.get(idTokenHint) : undefined)
  // The effect re-arms when redirectUri changes; once the timer has fired the
  // cookies are gone and a re-arm would send a second, hint-less logout
  const hasLoggedOut = useRef(false)

  useEffect(() => {
    if (hasLoggedOut.current) {
      return
    }

    // Hydra only accepts an exact, whitelisted post_logout_redirect_uri, so
    // the "why" of this logout can't travel via query params to /expired.
    // Stash it in sessionStorage, which survives the Hydra round-trip in the
    // same tab and is untouched by the localStorage wipe below.
    const reason = new URLSearchParams(window.location.search).get('reason')
    if (reason === LOGOUT_REASON_EXPIRED) {
      sessionStorage.setItem(LOGOUT_REASON_KEY, LOGOUT_REASON_EXPIRED)
    } else {
      sessionStorage.removeItem(LOGOUT_REASON_KEY)
    }

    const logoutTimeout = setTimeout(async () => {
      hasLoggedOut.current = true
      // redirect to custom url after going to /expired page when logout
      // this is used on /session-conflict page from payments app
      const rawPostExpiredRedirectUri = window.location.search
        ?.split('post_expired_redirect_to=')[1]
        ?.split('&')[0]
      // Validate protocol before embedding — domain may be a custom partner domain
      const postExpiredRedirectUri = rawPostExpiredRedirectUri
        ? validateRedirectProtocol(rawPostExpiredRedirectUri)
        : null

      // clean out cache and session token
      await client.cache.reset()
      clearLocalStorageExcept(preserveKeys)
      deleteCookies()

      if (isImpersonation) {
        return
      }

      // The token cookies may be gone (expired-link restarts, cross-domain
      // session-conflict logouts) — the builder drops id_token_hint AND
      // post_logout_redirect_uri together in that case, since Hydra 400s
      // on a hint of "undefined" and on a redirect without a hint.
      const url = buildHydraLogoutUrl({
        hydraUrl: hydraUrl || process.env.NEXT_PUBLIC_HYDRA_URL || '',
        idToken: token,
        postLogoutRedirectUri: redirectUri,
        state: `${setRandomString()}${
          postExpiredRedirectUri ? `-${postExpiredRedirectUri}` : ''
        }`
      })

      // redirect to hydra logout
      window.location.href = url
    }, delay)

    return () => clearTimeout(logoutTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm only when the token or the post-logout target changes; the payments target depends on a company that loads after mount, and arming on the first render's fallback sends a redirect Hydra rejects on custom-domain portals
  }, [token, redirectUri])
}
